import React from 'react';
import Button, { LoadingButton } from '@atlaskit/button';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import ModalDialog, {
	ModalTitle,
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { useCommonPageContext } from '@atlassian/jira-servicedesk-common-page-context';
import {
	useFormDirty,
	useManageTranslationsActions,
	useTranslationStoreState,
} from '../../controllers/manage-translations/index.tsx';
import { useNavigationBlockModal } from '../../controllers/navigation-block-modal';
import { useTranslationsSaveDataService } from '../../services/save-data';
import type { LanguageTranslationsSavePayload } from '../../services/save-data/types';
import { buildTranslationsSaveDataPayload } from '../../services/utils';
import messages from './messages';

export const UnsavedChangesModal = () => {
	const { formatMessage } = useIntl();
	const { selectedPanelId, translationPanelMap, currentLanguage } = useTranslationStoreState();
	const { discardChanges, resetStoreAfterSave } = useManageTranslationsActions();
	const { projectKey } = useCommonPageContext();
	const { showFlag } = useFlagsService();

	const isFormDirty = useFormDirty();
	const shouldModalBeShown = isFormDirty;

	const { isModalOpen, closeModal } = useNavigationBlockModal({ shouldModalBeShown });

	const onFailure = (err: unknown) => {
		const errorMessage =
			err instanceof FetchError && err.statusCode === 403
				? messages.disabledErrorMessage
				: messages.genericErrorMessage;
		showFlag({
			type: 'error',
			isAutoDismiss: true,
			title: formatMessage(errorMessage),
		});
		closeModal();
	};

	const onSuccess = () => {
		showFlag({
			type: 'success',
			isAutoDismiss: true,
			title: formatMessage(messages.translationsSaveSuccess),
		});
		resetStoreAfterSave();
		closeModal();
	};

	const [{ loading: isSaveDataLoading }, { saveLanguageTranslationsData }] =
		useTranslationsSaveDataService({
			projectKey,
			onFailure,
			onSuccess,
		});

	const handleSaveClick = () => {
		if (translationPanelMap && selectedPanelId && saveLanguageTranslationsData) {
			const payload: LanguageTranslationsSavePayload = buildTranslationsSaveDataPayload({
				selectedPanelId,
				translationPanelMap,
				currentLanguage,
			});

			saveLanguageTranslationsData(payload);
		}
	};

	const handleDiscardClick = () => {
		discardChanges();
		closeModal();
	};

	const handleCancelClick = () => {
		closeModal();
	};

	return (
		<ModalTransition>
			{isModalOpen && (
				<ShortcutScope>
					<ModalDialog>
						<ModalHeader>
							<ModalTitle>{formatMessage(messages.title)}</ModalTitle>
						</ModalHeader>
						<ModalBody>{formatMessage(messages.body)}</ModalBody>
						<ModalFooter>
							<LoadingButton
								appearance="primary"
								isLoading={isSaveDataLoading}
								onClick={handleSaveClick}
							>
								{formatMessage(messages.saveButton)}
							</LoadingButton>
							<Button appearance="subtle" onClick={handleDiscardClick}>
								{formatMessage(messages.discardButton)}
							</Button>
							<Button appearance="link" onClick={handleCancelClick}>
								{formatMessage(messages.cancelButton)}
							</Button>
						</ModalFooter>
					</ModalDialog>
				</ShortcutScope>
			)}
		</ModalTransition>
	);
};
